import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  Close,
  CameraAlt,
  PhotoLibrary,
  CompareArrows,
  Share,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';
import StyleCard from './StyleCard';
import tryOnService from '../services/tryOnService';
import replicateService from '../services/replicateService';
import ailabToolsService from '../services/ailabToolsService';
import { getStylesForCategory } from '../config/tryOnStyles';

const TryOnDialog = ({ open, onClose, category = 'haircut' }) => {
  const [activeTab, setActiveTab] = useState(0); // 0: camera, 1: upload
  const [userImage, setUserImage] = useState(null);
  const [styles, setStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStyles, setLoadingStyles] = useState(true);
  // AILabTools API handles detection automatically - users can upload hair photos or face photos
  const [imageReady, setImageReady] = useState(false); // Changed from faceDetected to imageReady
  const [error, setError] = useState(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize when dialog opens
  useEffect(() => {
    if (open) {
      // Check if API is configured
      if (!replicateService.isConfigured()) {
        setError('Replicate API key not configured. Please set REACT_APP_REPLICATE_API_KEY in your .env file.');
      } else {
        setError(null);
      }
      
      loadStyles();
    } else {
      // Reset state when dialog closes
      resetState();
    }

    return () => {
      stopCamera();
    };
  }, [open, category]);

  // Load styles from API or use defaults
  const loadStyles = async () => {
    try {
      setLoadingStyles(true);
      
      // Try to load from Replicate API (or use defaults)
      let apiStyles = [];
      if (replicateService.isConfigured()) {
        try {
          if (category === 'haircut') {
            apiStyles = replicateService.getDefaultHairstyles();
          } else if (category === 'makeup') {
            apiStyles = replicateService.getDefaultMakeupStyles();
          }
        } catch (apiError) {
          console.warn('Failed to load styles, using defaults:', apiError);
        }
      }

      // Use API styles if available, otherwise use defaults
      if (apiStyles && apiStyles.length > 0) {
        console.log(`✅ Loaded ${apiStyles.length} styles from API`);
        // Map API styles to our format (handle different API response formats)
        // API style names must match exactly (e.g., "BobCut", "PixieCut")
        const mappedStyles = apiStyles.map(apiStyle => {
          // Get the actual API style name (this is what we'll send to API)
          const apiStyleName = apiStyle.id || apiStyle.styleId || apiStyle.style_id || apiStyle.hairstyle_id || apiStyle.name;
          
          return {
            styleId: apiStyleName, // Use API style name as ID
            name: apiStyle.name || apiStyle.style_name || apiStyle.hairstyle_name || apiStyleName || 'Style',
            thumbnailUrl: apiStyle.thumbnail || apiStyle.thumbnailUrl || apiStyle.thumbnail_url || apiStyle.image_url || apiStyle.image || '/placeholder-style.jpg',
            category: category,
            description: apiStyle.description || apiStyle.desc || '',
            price: apiStyle.price || null,
            apiStyleId: apiStyleName // This is the exact name API expects
          };
        });
        setStyles(mappedStyles);
      } else {
        console.log('⚠️ No API styles, using defaults');
        // Use default/fallback styles
        const defaultStyles = getStylesForCategory(category);
        setStyles(defaultStyles);
      }
    } catch (error) {
      console.error('Error loading styles:', error);
      // Fallback to default styles
      const defaultStyles = getStylesForCategory(category);
      setStyles(defaultStyles);
    } finally {
      setLoadingStyles(false);
    }
  };

  const resetState = () => {
    setUserImage(null);
    setSelectedStyle(null);
    setPreviewImage(null);
    setImageReady(false);
    setError(null);
    setActiveTab(0);
    stopCamera();
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera access denied:', error);
      setError('Camera access denied. Please allow camera access or use photo upload instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const imageUrl = URL.createObjectURL(blob);
        setUserImage(imageUrl);
        stopCamera();
        detectFaceInImage(imageUrl);
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setError('Failed to capture photo. Please try again.');
    }
  };

  // Photo upload functions
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Validate image
      await tryOnService.validateImage(file);
      
      const imageUrl = URL.createObjectURL(file);
      setUserImage(imageUrl);
      setError(null);
      await detectFaceInImage(imageUrl);
    } catch (error) {
      setError(error.message || 'Failed to load image. Please try another photo.');
    }
  };

  const detectFaceInImage = async (imageUrl) => {
    try {
      setLoading(true);
      
      // AILabTools API handles detection automatically
      // Users can upload hair photos or face photos - API will handle it
      if (!ailabToolsService.isConfigured()) {
        setError('AILabTools API key not configured. Please set REACT_APP_AILAB_API_KEY in your .env file.');
        setImageReady(false);
        return;
      }

      // Image is ready - API will handle detection when applying style
      setImageReady(true);
      setError(null);
    } catch (error) {
      console.error('Error validating image:', error);
      setImageReady(false);
      setError('Failed to process image. Please try another photo.');
    } finally {
      setLoading(false);
    }
  };

  // Style selection
  const handleStyleSelect = async (style) => {
    if (!ailabToolsService.isConfigured()) {
      setError('AILabTools API key not configured. Please set REACT_APP_AILAB_API_KEY in your .env file.');
      return;
    }

    if (!userImage) {
      setError('Please upload a photo first.');
      return;
    }

    setSelectedStyle(style);
    setLoading(true);
    setError(null);

    try {
      // API will handle face detection and validation
      const preview = await tryOnService.applyStyle(userImage, style);
      setPreviewImage(preview);
      setImageReady(true); // Mark as ready if API succeeds
    } catch (error) {
      console.error('Error applying style:', error);
      setError(error.message || 'Failed to apply style. Please try another photo.');
      setPreviewImage(null);
      setImageReady(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAnother = () => {
    setSelectedStyle(null);
    setPreviewImage(null);
    setError(null);
  };

  const handleShare = async () => {
    if (!previewImage) return;

    try {
      // Convert data URL to blob
      const response = await fetch(previewImage);
      const blob = await response.blob();
      const file = new File([blob], 'try-on-preview.jpg', { type: 'image/jpeg' });

      if (navigator.share) {
        await navigator.share({
          title: 'My Try-On Preview',
          text: 'Check out how this looks on me!',
          files: [file]
        });
      } else {
        // Fallback: copy to clipboard or download
        const link = document.createElement('a');
        link.href = previewImage;
        link.download = 'try-on-preview.jpg';
        link.click();
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh', 
          maxHeight: '900px',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            AI Try-On: {category === 'haircut' ? 'Haircut' : 'Makeup'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
            icon={<ErrorOutline />}
          >
            {error}
          </Alert>
        )}

        {/* PHASE 1: Photo Upload */}
        {!userImage && (
          <Box>
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => {
                setActiveTab(v);
                if (v === 0) {
                  stopCamera();
                }
              }}
              sx={{ mb: 2 }}
            >
              <Tab icon={<CameraAlt />} label="Camera" />
              <Tab icon={<PhotoLibrary />} label="Upload" />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {activeTab === 0 ? (
                <Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxHeight: '400px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: '#f5f5f5',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2
                    }}
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      style={{ 
                        width: '100%', 
                        maxHeight: '400px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button 
                      variant="outlined" 
                      onClick={startCamera}
                      startIcon={<CameraAlt />}
                    >
                      Start Camera
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={capturePhoto}
                      disabled={!streamRef.current}
                    >
                      Capture Photo
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload">
                    <Button 
                      variant="contained" 
                      component="span"
                      size="large"
                      startIcon={<PhotoLibrary />}
                    >
                      Choose Photo
                    </Button>
                  </label>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Upload a clear photo of your face
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* PHASE 2: Style Selection */}
        {userImage && !selectedStyle && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Choose a Style:
              </Typography>
              {!ailabToolsService.isConfigured() && (
                <Chip 
                  label="API Key Required" 
                  color="error" 
                  size="small"
                  icon={<ErrorOutline />}
                />
              )}
            </Box>

            {/* User's photo preview */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  display: 'inline-block', 
                  p: 1,
                  borderRadius: 2
                }}
              >
                <img 
                  src={userImage} 
                  alt="Your photo" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '200px',
                    borderRadius: 8,
                    objectFit: 'cover'
                  }} 
                />
                <Button
                  size="small"
                  onClick={() => {
                    setUserImage(null);
                    setImageReady(false);
                    setError(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  Change Photo
                </Button>
              </Paper>
            </Box>

            {loadingStyles ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {styles.map((style) => (
                  <Grid item xs={6} sm={4} md={3} key={style.styleId}>
                    <StyleCard
                      style={style}
                      selected={selectedStyle?.styleId === style.styleId}
                      onClick={() => handleStyleSelect(style)}
                      disabled={!ailabToolsService.isConfigured() || !userImage}
                      loading={loading && selectedStyle?.styleId === style.styleId}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {styles.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No styles available for this category.
              </Typography>
            )}
          </Box>
        )}

        {/* PHASE 3: Preview */}
        {selectedStyle && previewImage && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Preview: {selectedStyle.name}
              </Typography>
              <Button
                size="small"
                onClick={handleTryAnother}
                startIcon={<CompareArrows />}
              >
                Try Another Style
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Before
                  </Typography>
                  <img 
                    src={userImage} 
                    alt="Original" 
                    style={{ 
                      width: '100%', 
                      maxHeight: '400px',
                      borderRadius: 8,
                      objectFit: 'contain'
                    }} 
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    After
                  </Typography>
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    style={{ 
                      width: '100%', 
                      maxHeight: '400px',
                      borderRadius: 8,
                      objectFit: 'contain'
                    }} 
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {loading && !previewImage && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Processing...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        
        {userImage && !selectedStyle && (
          <Button 
            onClick={() => {
              setUserImage(null);
              setImageReady(false);
              setError(null);
            }}
          >
            Change Photo
          </Button>
        )}

        {previewImage && (
          <>
            <Button
              startIcon={<Share />}
              onClick={handleShare}
            >
              Share
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Could add to cart or save preference here
                onClose();
              }}
            >
              Save Preview
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TryOnDialog;
